import moment from 'moment';
import { USER_ROLES } from './constant';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';
import imageToBase64 from 'image-to-base64';
import { showSuccess } from './notifications';

const reg = /.+?\:\/\/.+?(\/.+?)(?:#|\?|$)/;
const imageKitBaseURL = 'https://ik.imagekit.io/ykmmrc3nwl';
export const DEFAULT_PLACEHOLDER = 'https://ik.imagekit.io/ykmmrc3nwl/product-placeholder_WjZYBtQ7H.png';

export const capitalize = (text: string) => {
  const textArr = text.split(' ');
  for (let i = 0; i < textArr.length; i++) {
    textArr[i] = textArr[i].substr(0, 1).toUpperCase() + textArr[i].substr(1).toLowerCase();
  }
  return textArr.join(' ');
};

export const isValidEmail = (email: string) => {
  const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
};

export const isValidPhone = (phone: string) => {
  const regex = /^([7-9][0-9]{9})$/;
  return regex.test(String(phone));
};

export const validate = (type: string, value: any, field?: string, extraValue?: string) => {
  let error = '';
  switch (type) {
    case 'emailOrPhone':
      error = validate('email', value);
      if (error) {
        error = validate('phone', value);
      }
      if (error) {
        error = 'Please enter valid email or phone';
      }
      break;
    case 'email':
      if (!value || !isValidEmail(value)) {
        error = 'Please enter valid email.';
      }
      break;

    case 'password':
      if (!value) {
        error = 'Password must not be empty.';
      }
      break;

    case 'cpassword':
      if (value !== extraValue) {
        error = 'Passwords do not match.';
      }
      break;

    case 'phone':
      if (value && !isValidPhone(value)) {
        error = `Please enter valid phone number.`;
      }
      break;

    case 'required':
      if (!value) {
        error = `${field} can't be empty.`;
      }
      break;

    default:
      break;
  }
  return error;
};

export const formatObject = (obj: any) => {
  for (const key in obj) {
    if (!obj[key]) {
      delete obj[key];
    }
  }
  return obj;
};

export const formatDate = (date: any, format = 'l') => {
  return moment(date).format(format);
};

export const isUserAuthorized = (userRoles: string[] = [], authorizedRoles: string[] = []) => {
  // Empty authorizedRoles means we just need a logged in user.
  // Admin has access to all the routes
  if (_.isEmpty(authorizedRoles) || _.indexOf(userRoles, USER_ROLES.ADMIN) !== -1) {
    return true;
  }
  for (const userRole of userRoles) {
    for (const role of authorizedRoles) {
      if (role === userRole) return true;
    }
  }
  return false;
};

export const roundNumber = (number: number, decimalPlaces: number) => number.toFixed(decimalPlaces);

export const formatAddress = (addressData: any) => (addressData?.address || addressData?.addressObject?.address) + ' ' + (addressData?.secondAddress || addressData?.addressObject?.secondAddress);

export const isDeviceMobile = () => {
  let isMobile = false;
  const browserData = navigator.userAgent || navigator.vendor;
  if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(browserData) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(browserData.substr(0, 4))) isMobile = true;
  return isMobile;
};

export const cloneCanvas = (oldCanvas) => {
  //create a new canvas
  const newCanvas = document.createElement('canvas');
  const context = newCanvas.getContext('2d');

  //set dimensions
  newCanvas.width = oldCanvas.width;
  newCanvas.height = oldCanvas.height;

  //apply the old canvas to the new one
  if (context)
    context.drawImage(oldCanvas, 0, 0);

  //return the new canvas
  return newCanvas;
};

export const metersToMiles = (meters: number) => {
  return meters * 0.000621371;
};

export const secondsToMinutes = (seconds: number) => {
  if (!seconds) return null;
  return Math.floor(seconds / 60);
};

export const convertToLocaleString = (value) => {
  return (value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export const getEmailsArray = (emails: string) => {
  if (emails) {
    return emails.split(',').map(email => email.trim()?.toLowerCase());
  }
  return [];
};

export const sortArray = (array: any[], sortBy = '') => {
  if (sortBy) return _.sortBy(array, sortBy);
  else return array.sort();
};

export const getNumericalArray = (limit: number, initialValue = 1) =>
  Array.from({ length: limit }, (_, i) => i + initialValue);

export const getAlphabetArray = (limit = 26) =>
  getNumericalArray(limit, 97).map((x) => String.fromCharCode(x));

export const getDistanceInMiles = (meters) => {
  return _.isNumber(meters) ? `${_.round(metersToMiles(meters), 2)} mi` : '-';
};

export const getSecondsDurationInHoursMins = (seconds) => {
  return _.isNumber(seconds) ? new Date(seconds * 1000).toISOString().substr(11, 8) : '-';
};

export const removeWhiteSpacesAndNonNumberCharacters = (string: string) => {
  return string.replace(/[^0-9]+/ig, '');
};

export const convertFrom24To12HrFormat = (timeString: string, includeTimePeriod: boolean) => {
  return moment(timeString, ['HH:mm']).format(`hh:mm ${includeTimePeriod ? 'A' : ''}`);
};

const getImageName = (url): string => {
  const pathnames = (((reg.exec(url) || [])[1]) || '').split('/');
  return pathnames[pathnames.length - 1];
};

export const getSearchImagePlaceholder = (imageName: string, blur?: string) => {
  imageName = imageName ? imageName : getImageName(DEFAULT_PLACEHOLDER);
  const placeholder = imageName ? `${imageKitBaseURL}/${imageName}?tr=bl-${blur || 6},w-${250},h-${250}` : DEFAULT_PLACEHOLDER;
  return placeholder;
};

export const useQueryParams = () => {
  return new URLSearchParams(useLocation().search);
};

export const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

export const CentsToDollar = amt => {
  return amt / 100;
};
export const imageurlToBase64 = async (url) => {
  try {
    const base64String = await imageToBase64(url);
    return `data:image/png;base64,${base64String}`;
  } catch (e) {
    console.log(e);
    return e;
  }
};
export const isBase64 = (arg) => {
  if (arg === '' || arg?.trim() === '') { return false }
  try {
    const str = arg?.split(',').splice(-1);
    return btoa(atob(str)) == str;
  } catch (err) {
    return false;
  }
};

export const isValidURL = (string) => {
  const res = string.match(/(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/);
  return (res !== null);
};

export const sanitizeFilterObject = (filterData: any) => {
  return _.pickBy(filterData, _.identity);
};

export const dollarToCents = amt => {
  return amt.toFixed(2) * 100;
};

export const stringifyAndParseObj = (obj) => {
  if (obj) {
    return JSON.parse(JSON.stringify(obj));
  } else {
    return {};
  }
};

export const getImageUrlFromName = (imageName) => {
  return imageName ? `${imageKitBaseURL}/${imageName}` : DEFAULT_PLACEHOLDER;
};

export const sortAlphaNum = (rowA, rowB) => {
  if (rowA.slotNo && rowB.slotNo) {
    const [storageClassA, storageNoA] = [rowA.slotNo.slice(0, 1), parseInt(rowA.slotNo.slice(1), 10)];
    const [storageClassB, storageNoB] = [rowB.slotNo.slice(0, 1), parseInt(rowB.slotNo.slice(1), 10)];
    if (storageClassA === storageClassB) {
      return (storageNoA === storageNoB) ? 0 : (storageNoA > storageNoB) ? 1 : -1;
    } else {
      return (storageClassA > storageClassB) ? 1 : -1;
    }
  } else {
    return 1;
  }
};

export const timeToString = (time: number) => {
  const days = Math.floor(time / 1440);
  const hours = Math.floor(time / 60);
  const minutes = Math.floor(time % 60);
  return (!days && !minutes && !hours) ? '<1min' : `${days ? days + ' d' : ''} ${hours ? hours + ' hr' : ''} ${minutes ? minutes + ' min' : ''}`;
};

export const formatInventoryItemAddressWith0 = (address) => {
  //this function add 0 to the number after decimal if that number is below 10, eg: 9 -> 09, 2 -> 02
  if (!address) return '';
  const splittedAddress = address.split('.');
  // if length of splitted address is less than 0 then this is a whole number , not a float so we are returning it as it is
  if (splittedAddress?.length < 2) {
    return address;
  }
  const numberAfterDecimal = parseInt(splittedAddress[1]);
  const formatedNumberAfterDecimal = numberAfterDecimal < 10 ? `0${numberAfterDecimal}` : numberAfterDecimal;
  return splittedAddress[0] + '.' + formatedNumberAfterDecimal;
};

export const inventoryItemAddressRangeFormat = (inventoryItemAddress: string) => {
  const arr = inventoryItemAddress ? inventoryItemAddress?.split('-') : [];
  const formattedStartAddress = formatInventoryItemAddressWith0(arr[0]);
  const formattedEndAddress = formatInventoryItemAddressWith0(arr[1]);
  if (formattedStartAddress === formattedEndAddress || arr?.length < 2) {
    return formattedStartAddress;
  } else {
    return formattedStartAddress + '-' + formattedEndAddress;
  }
};

export const sanitizeInventoryItemsWithInventoryAddress = (inventoryItems) => {
  const itemsWithSanitizedInventoryAddress = inventoryItems?.map((item) => {
    if (!item?.inventoryAddress) {
      return item;
    }
    return {
      ...item,
      inventoryAddress: inventoryItemAddressRangeFormat(item.inventoryAddress),
    };
  });
  return itemsWithSanitizedInventoryAddress;
};

export const getDifferenceBetweenDates = (dateOne, dateTwo, unit = 'minutes') => {
  dateOne = dateOne?.iso ? dateOne.iso : dateOne;
  dateTwo = dateTwo?.iso ? dateTwo.iso : dateTwo;
  dateOne = moment(dateOne);
  dateTwo = moment(dateTwo);
  return dateOne.diff(dateTwo, unit);
};

export const getSanitizedLocationCoordsForAddress = (locationCoords) => {
  return { lat: locationCoords?.latitude ?? locationCoords?.lat, lng: locationCoords?.longitude ?? locationCoords?.lng };
};

export const canItemBeSyncedInSalesChannel = ({ isSyncPending }) => {
  return isSyncPending;
};

export const sanitizePhoneNumber = (value) => {
  return value.replace(/["'()-]/g, '');
};

export const isValidPhoneNumber = (value) => {
  const sanitizedPhoneNumber = value.replace(/["'()-]/g, '');
  return sanitizedPhoneNumber?.length === 10 ? sanitizedPhoneNumber : '';
};

export const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader: any = new FileReader();
    reader.onload = () => resolve(reader?.result?.split(',')[1]); // Extract base64 content
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

export const copyToClipboard = (copyText: string, copySuccessMessage?: string) => {
  try {
    const ta = document.createElement('textarea');
    ta.innerText = copyText;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    ta.remove();
    showSuccess(copySuccessMessage || 'Copied to Clipboard');
  } catch (e) {
    console.log(e);
  }
};